import { useMemo, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import {
  ConflictSegment,
  GuideConflict,
  useConflicts,
  useResolveAllConflicts,
  useResolveConflict,
  useResolveGuideConflict,
} from "@/hooks/useConflicts";

interface Props {
  slug: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Resolution queue for passages that changed both in the app and in the repo
 * since the last sync. Pushing is blocked while any remain, so each must be
 * resolved — keep yours, take the repo's, or hand-merge — before the next sync
 * can push. Bulk buttons resolve every conflict to one side at once.
 */
export function ConflictReviewPanel({ slug, open, onClose }: Props) {
  const { data, isLoading } = useConflicts(slug, open);
  const resolve = useResolveConflict(slug);
  const resolveGuide = useResolveGuideConflict(slug);
  const resolveAll = useResolveAllConflicts(slug);
  const busy =
    resolve.isPending || resolveGuide.isPending || resolveAll.isPending;

  // Group the flat queue by chapter, preserving the server's chapter→position order.
  const groups = useMemo(() => {
    const out: { title: string; items: ConflictSegment[] }[] = [];
    for (const s of data?.conflictSegments ?? []) {
      const title = s.chapterTitle ?? "—";
      const last = out[out.length - 1];
      if (last && last.title === title) last.items.push(s);
      else out.push({ title, items: [s] });
    }
    return out;
  }, [data]);

  if (!open) return null;
  const count = data?.conflictCount ?? 0;
  const bookId = data?.id;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">
            Conflicts to resolve ({count})
          </h2>
          <div className="flex items-center gap-2">
            {count > 0 && bookId && (
              <>
                <button
                  onClick={() =>
                    resolveAll.mutate({ bookId, resolution: "mine" })
                  }
                  disabled={busy}
                  className="rounded-full border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  Keep all mine
                </button>
                <button
                  onClick={() =>
                    resolveAll.mutate({ bookId, resolution: "repo" })
                  }
                  disabled={busy}
                  className="rounded-full border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  Use all repo
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-800"
            >
              ✕
            </button>
          </div>
        </div>
        <p className="mb-4 text-xs text-slate-500">
          These passages changed both here and in the repo since the last sync.
          Syncing is paused until each is resolved — keep your version, take the
          repo’s, or edit a merge.
        </p>

        {isLoading && <p className="text-sm text-slate-400">Loading…</p>}
        {!isLoading && count === 0 && (
          <p className="text-sm text-slate-500">
            No conflicts — you’re clear to sync. ✅
          </p>
        )}

        {(data?.guideConflicts?.length ?? 0) > 0 && bookId && (
          <section className="mb-4">
            <h3 className="mb-2 text-sm font-medium text-slate-700">
              Translation guides
            </h3>
            <ul className="space-y-2">
              {data!.guideConflicts.map((g) => (
                <GuideConflictRow
                  key={g.key}
                  guide={g}
                  busy={busy}
                  onResolve={(resolution, mergedText) =>
                    resolveGuide.mutate({
                      bookId,
                      key: g.key,
                      resolution,
                      mergedText,
                    })
                  }
                />
              ))}
            </ul>
          </section>
        )}

        {groups.map((g) => (
          <section key={g.title} className="mb-4">
            <h3 className="mb-2 text-sm font-medium text-slate-700">
              {g.title}
            </h3>
            <ul className="space-y-2">
              {g.items.map((s) => (
                <ConflictRow
                  key={s.id}
                  segment={s}
                  busy={busy}
                  onResolve={(resolution, mergedText) =>
                    resolve.mutate({ segmentId: s.id, resolution, mergedText })
                  }
                />
              ))}
            </ul>
          </section>
        ))}

        {(resolve.isError || resolveGuide.isError || resolveAll.isError) && (
          <p className="mt-2 text-xs text-red-600">
            Couldn’t apply that — try again.
          </p>
        )}
      </div>
    </div>
  );
}

function ConflictRow({
  segment,
  busy,
  onResolve,
}: {
  segment: ConflictSegment;
  busy: boolean;
  onResolve: (
    resolution: "mine" | "repo" | "merge",
    mergedText?: string,
  ) => void;
}) {
  return (
    <ResolutionRow
      header={segment.externalId}
      mine={segment.targetText}
      repo={segment.conflictRepoText}
      emptyLabel="∅ (untranslated)"
      busy={busy}
      onResolve={onResolve}
    />
  );
}

function GuideConflictRow({
  guide,
  busy,
  onResolve,
}: {
  guide: GuideConflict;
  busy: boolean;
  onResolve: (
    resolution: "mine" | "repo" | "merge",
    mergedText?: string,
  ) => void;
}) {
  return (
    <ResolutionRow
      header={guide.label}
      mine={guide.localText}
      repo={guide.repoText}
      emptyLabel="∅ (empty)"
      mono
      busy={busy}
      onResolve={onResolve}
    />
  );
}

// Shared keep-mine / use-repo / hand-merge UI for one conflict, used by both
// segment and guide rows. `mono` renders the text monospaced (guides are
// markdown), otherwise serif (prose translations).
function ResolutionRow({
  header,
  mine,
  repo,
  emptyLabel,
  mono = false,
  busy,
  onResolve,
}: {
  header: string;
  mine: string | null;
  repo: string | null;
  emptyLabel: string;
  mono?: boolean;
  busy: boolean;
  onResolve: (
    resolution: "mine" | "repo" | "merge",
    mergedText?: string,
  ) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(mine ?? "");
  const textFont = mono ? "font-mono text-xs" : "font-serif text-sm";

  return (
    <li className="rounded-lg border border-amber-300 bg-amber-50/40 p-3">
      <div className="mb-2 text-xs text-slate-400">{header}</div>

      <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
        Yours
      </div>
      <p className={`mb-2 whitespace-pre-wrap text-slate-800 ${textFont}`}>
        {mine || <span className="text-slate-400">{emptyLabel}</span>}
      </p>

      <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
        Repo
      </div>
      <p className={`mb-3 whitespace-pre-wrap text-slate-600 ${textFont}`}>
        {repo || <span className="text-slate-400">{emptyLabel}</span>}
      </p>

      {editing ? (
        <div>
          <TextareaAutosize
            className={`w-full resize-none rounded-lg border border-slate-300 p-2 ${textFont}`}
            minRows={2}
            maxRows={14}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={() => onResolve("merge", draft)}
              disabled={busy || !draft.trim()}
              className="rounded-full bg-slate-900 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
            >
              Save merge
            </button>
            <button
              onClick={() => setEditing(false)}
              className="px-2 py-1 text-xs text-slate-500"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => onResolve("mine")}
            disabled={busy}
            className="rounded-full border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Keep mine
          </button>
          <button
            onClick={() => onResolve("repo")}
            disabled={busy}
            className="rounded-full border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Use repo
          </button>
          <button
            onClick={() => {
              setDraft(mine ?? "");
              setEditing(true);
            }}
            disabled={busy}
            className="rounded-full border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Edit…
          </button>
        </div>
      )}
    </li>
  );
}
